import { Parameter } from "domain/entities/parameter";
import { Vacation } from "domain/entities/vacations";
import { VacationStatusEnum } from "domain/enum/vacationStatus";
import { EmployeeInputFilter } from "domain/models/employee-input-filter";
import { ProjectInputFilter } from "domain/models/project-input-filter";
import { VacationInputFilter } from "domain/models/vacation-input-filter";
import DropdownComponent from "presentation/components/Shared/DropdownComponent";
import InputDateComponent from "presentation/components/Shared/InputDateComponent";
import Spinner from "presentation/components/Shared/Spinner";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import { useProjectsByFilter } from "presentation/hooks/use-query-project";
import {
  useUpdateVacationPlanMutation,
  useVacationPlanByFilter,
} from "presentation/hooks/use-query-vacation";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { formatDateToPT } from "presentation/utils/formatDateToPT";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { useEffect, useMemo, useRef, useState } from "react";
import { Chart } from "react-google-charts";
import "./index.css";

interface VacationSettingParams {
  managerId?: string;
  date?: Date;
  isAdmin?: boolean;
  isManager?: boolean;
  projectId?: string;
  vacationId?: string;
  task?: string;
  onClose?: () => void;
}

const getMonthRange = (rawDate: Date | string) => {
  const date = new Date(rawDate);
  const start = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
  const end = new Date(
    date.getFullYear(),
    date.getMonth() + 1,
    0,
    23,
    59,
    59,
    999,
  );
  return { start, end };
};

const statusLabels: Record<string, string> = {
  VacationId: "Período de Férias em Questão",
  Aprovado: VacationStatusEnum.APROVADO,
  "A Aprovar": VacationStatusEnum.A_APROVAR,
  "Não Aprovado": VacationStatusEnum.NAO_APROVADO,
  "Cancelamento Pendente": VacationStatusEnum.CANCELAMENTO_PENDENTE,
  Cancelado: VacationStatusEnum.CANCELADO,
};

const statusColors: Record<string, string> = {
  Aprovado: "#5cb85c",
  "A Aprovar": "#f0ad4e",
  "Não Aprovado": "#d9534f",
  "Cancelamento Pendente": "#abdbff",
  Cancelado: "#737373",
  VacationId: "#2c47d1",
  default: "#f0ad4e",
};

export const VacationSetting: React.FC<VacationSettingParams> = ({
  isAdmin,
  isManager,
  date,
  managerId,
  vacationId,
  onClose,
  task,
}) => {
  const isDashboard = managerId ? true : false;
  const [range, setRange] = useState(() => getMonthRange(date ?? new Date()));
  const [visibleRejectionReason, setVisibleRejectionReason] = useState(false);
  const [statusManager, setStatusManager] = useState(isManager);
  const [reasonReject, setReasonReject] = useState("");
  const [vacationSettingParameters, setVacationSettingParameters] =
    useState<VacationSettingParams>({
      date: date ?? new Date(),
      managerId: managerId ?? "todos",
      projectId: "",
    });
  const [selectedVacation, setSelectedVacation] = useState<any>(
    vacationId || null,
  );
  const vacationMap = new Map<string, any>();
  const employeeRowIndexMap: string[] = [];
  const toast = useRef<Toast>(null);

  const { userEmployee } = useAuthStore();
  const {
    data: vacationData,
    refetch: refetchVacationData,
    isLoading: isLoading,
    isRefetching: isRefetching,
  } = useVacationPlanByFilter({
    limit: 100,
    startRange: range.start,
    endRange: range.end,
  } as VacationInputFilter);
  const { data: employeeData } = useEmployeesByFilter({
    limit: 100,
    isActive: true,
  } as EmployeeInputFilter);
  const { data: projectData } = useProjectsByFilter({
    limit: 100,
  } as ProjectInputFilter);
  const mutation = useUpdateVacationPlanMutation();

  const visibleEmployees = useMemo(() => {
    if (!employeeData?.data) return [];

    let filtered = isAdmin
      ? employeeData.data
      : employeeData.data.filter(
        (emp) => emp.managerAssociated?.id === userEmployee?.id,
      );

    const selectedProjectId = vacationSettingParameters.projectId;
    if (
      selectedProjectId &&
      selectedProjectId !== "todos" &&
      vacationData?.data
    ) {
      const allowedEmployeeIds = new Set<string>();
      vacationData.data.forEach((vac) => {
        vac.projects?.forEach((proj) => {
          if (proj.id === selectedProjectId && vac.employee?.id) {
            allowedEmployeeIds.add(vac.employee.id);
          }
        });
      });
      filtered = filtered.filter((emp) => allowedEmployeeIds.has(emp.id));
    }

    return filtered;
  }, [
    employeeData?.data,
    vacationData?.data,
    isAdmin,
    userEmployee?.id,
    vacationSettingParameters.projectId,
  ]);

  const [managers, setManagers] = useState<Parameter[]>([]);
  const [projects, setProjects] = useState<Parameter[]>([]);

  const chartData: any[] = [
    [
      { type: "string", id: "Employee" },
      { type: "string", id: "Status" },
      { type: "string", role: "tooltip", p: { html: true } },
      { type: "string", id: "style", role: "style" },
      { type: "date", id: "Start" },
      { type: "date", id: "End" },
    ],
  ];

  const getShortName = (name: string) => {
    const parts = name.split(" ");
    if (parts.length > 2) {
      return `${parts[0]} ${parts[parts.length - 1]}`;
    }
    return name;
  };

  useEffect(() => {
    if (!employeeData?.data) return;
    const filteredManagers = employeeData.data
      .filter((emp) => emp.jobTitle?.isPeopleManager === true)
      .map((emp) => ({ id: emp.id, value: emp.name, type: "manager" }));
    setManagers([
      { id: "todos", value: "Todos", type: "manager" },
      ...filteredManagers,
    ]);
  }, [employeeData]);

  useEffect(() => {
    if (!projectData?.data) return;
    const filteredProjects = projectData.data.map((proj) => ({
      id: proj.id,
      value: proj.name,
      type: "project",
    }));
    setProjects([...filteredProjects]);
  }, [projectData]);

  const colors: string[] = [];

  if (employeeData?.data) {
    const allEmployees = employeeData?.data || [];
    const employeeMap = Object.fromEntries(
      allEmployees.map((emp) => [emp.id, emp]),
    );
    const { start: startOfMonth, end: endOfMonth } = getMonthRange(
      vacationSettingParameters.date ?? new Date(),
    );

    chartData.push(["Colaborador", " ", "", "", startOfMonth, endOfMonth]);
    colors.push("#fff");

    const managersWithVacations = new Set<string>();
    vacationData?.data?.sort((a, b) => {
      const nameA = a.employee?.name || "";
      const nameB = b.employee?.name || "";
      return nameA.localeCompare(nameB);
    });

    vacationData?.data?.forEach((vac) => {
      const employeeId = vac?.employee?.id;
      if (!employeeId) return;
      const employee = employeeMap[employeeId];
      if (!employee) return;

      const isVisible = visibleEmployees.some((e) => e.id === employeeId);

      if (!isVisible) return;

      const selectedProjectId = vacationSettingParameters.projectId || "";
      if (
        selectedProjectId &&
        !vac.projects?.some((p) => p.id === selectedProjectId)
      ) {
        return;
      }

      employeeRowIndexMap.push(employee.id);
      const id = vac.id || "";
      vacationMap.set(id, vac);

      const managerName = employee.managerAssociated?.name ?? "Sem gestor";
      const managerId = employee.managerAssociated?.id;
      const selectedManagerId = vacationSettingParameters.managerId || "todos";
      const shouldShow =
        !selectedManagerId || selectedManagerId === "todos"
          ? true
          : managerId === selectedManagerId;

      const vacationStart = new Date(vac.startAt ?? "");
      const dias = vac.days;
      vacationStart.setHours(0, 0, 0, 0);
      const vacationEnd = new Date(vac.endAt ?? "");
      vacationEnd.setHours(23, 59, 59, 999);

      const overlaps =
        vacationStart <= endOfMonth && vacationEnd >= startOfMonth;
      const status = vac.status;
      if (!status) return;

      if (shouldShow && overlaps) {
        managersWithVacations.add(managerName);

        let nextYearWarning = "";
        if (vac.daysNextYear && vac.daysNextYear > 0) {
          nextYearWarning =
            vac.daysNextYear > 0
              ? `
                <div style="margin-top:6px; padding:6px; background:#fff3cd; border-radius:4px;">
                  ⚠️ <b>Aviso:</b> ${vac.daysNextYear} ${vac.daysNextYear === 1 ? "dia pertence" : "dias pertencem"
                        } ao ano civil seguinte.
                </div>
              `
              : "";
        }


        const plural = dias === 1 ? "dia" : "dias";

        const statusLabel =
          Object.keys(statusLabels).find(
            (key) => statusLabels[key] === status,
          ) ?? "A Aprovar";

        const tooltip = `
          <div style="padding:10px; width:260px">
            <span #id style="display: none">${vac.id}</span>
            <b>Colaborador:</b> ${employee.name}<br/>
            <b>Manager:</b> <span>${managerName}<br/> 
            <b>Período:</b> ${formatDateToPT(vacationStart)} a ${formatDateToPT(
          vacationEnd,
        )}<br/>
            <b>Duração:</b> ${dias} ${plural}<br/>
            <b>Status:</b> ${statusLabel}
            ${nextYearWarning}
          </div>
        `;

        // const name = getLabelAdjustedToBar(employee.name, dias);
        const name = getShortName(employee.name);

        const isSelectedVacation = vac.id === vacationId;

        const barColor = isSelectedVacation
          ? statusColors["VacationId"]
          : (statusColors[statusLabel] ?? statusColors.default);

        chartData.push([
          name,
          statusLabel,
          tooltip,
          barColor,
          vacationStart,
          vacationEnd,
        ]);
        colors.push(barColor);
        vacationMap.set(employee.id, vac);
      }
    });
  }

  useEffect(() => {
    if (isManager && userEmployee?.id) {
      const statusManager = ["Manager", "CEO"].includes(
        userEmployee?.jobTitle?.name,
      );
      setVacationSettingParameters((prev) => ({
        ...prev,
        managerId: userEmployee.id,
        isManager: statusManager,
      }));
      setStatusManager(statusManager);
    }
  }, [userEmployee?.id]);

  const handleApproveOrReject = async (status: string) => {
    if (!selectedVacation) return;

    if (
      (task == "Pedido" &&
        status == VacationStatusEnum.NAO_APROVADO &&
        reasonReject.trim() === "") ||
      (task == "Cancelamento" &&
        status == VacationStatusEnum.APROVADO &&
        reasonReject.trim() === "")
    ) {
      return;
    }
    setVisibleRejectionReason(false);

    let rejectionReasonToSend = undefined;
    if (
      (task == "Pedido" && status == VacationStatusEnum.NAO_APROVADO) ||
      (task == "Cancelamento" && status == VacationStatusEnum.APROVADO)
    )
      rejectionReasonToSend = reasonReject;

    try {
      await mutation.mutateAsync({
        id: selectedVacation,
        status: status,
        rejectionReason: rejectionReasonToSend,
      } as Vacation);

      setReasonReject("");
      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: "Solicitação de férias atualizada com sucesso.",
        life: 6000,
      });
      if (onClose) onClose();
    } catch (error: any) {
      const defaultMessage = "Erro ao atualizar solicitação de férias.";
      let errors: string[] = [];

      try {
        errors = JSON.parse(error.message);
      } catch {
        errors = [defaultMessage];
      }

      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: errors.join("\n"),
        life: 6000,
      });
    }
  };

  useEffect(() => {
    const newRange = getMonthRange(
      vacationSettingParameters.date ?? new Date(),
    );
    setRange(newRange);
  }, [vacationSettingParameters.date]);

  useEffect(() => {
    refetchVacationData();
  }, [range]);

  if (isLoading || isRefetching) return <Spinner />;

  return (
    <div className="timesheet-grid">
      {isAdmin && (
        <>
          <div className="timesheet-1-col">
            <DropdownComponent
              options={managers}
              label="Gestor"
              isValueId={true}
              fieldName="managerId"
              currentObject={vacationSettingParameters}
              getValue={vacationSettingParameters.managerId ?? "todos"}
              setValue={(prev) => {
                const value =
                  typeof prev.managerId === "object"
                    ? prev.managerId?.id
                    : prev.managerId;
                setVacationSettingParameters((curr) => ({
                  ...curr,
                  managerId: value,
                }));
              }}
              showClear={true}
            />
          </div>
          <div className="timesheet-1-col">
            <DropdownComponent
              options={projects}
              label="Projetos"
              isValueId={true}
              fieldName="projectId"
              currentObject={vacationSettingParameters}
              getValue={vacationSettingParameters.projectId ?? ""}
              setValue={(prev) => {
                const value =
                  typeof prev.projectId === "object"
                    ? prev.projectId?.id
                    : prev.projectId;
                setVacationSettingParameters((curr) => ({
                  ...curr,
                  projectId: value,
                }));
              }}
              showClear={true}
            />
          </div>
        </>
      )}

      {!isDashboard && (
        <div className="timesheet-1-col">
          <InputDateComponent
            label="Mês"
            fieldName="date"
            currentObject={vacationSettingParameters}
            getValue={vacationSettingParameters.date}
            setValue={setVacationSettingParameters}
            view="month"
            dateFormat="yy/mm"
          />
        </div>
      )}
      {!isDashboard && <div></div>}

      {!isLoading && !isRefetching && (
        <>
          {statusManager && chartData.length <= 2 ? (
            <div className="timesheet-4-cols">
              <div style={{ marginTop: "2rem", color: "#888" }}>
                {isAdmin
                  ? "Nenhum colaborador sob a gestão deste manager tem férias marcadas neste período."
                  : "Nenhum colaborador sob a sua gestão tem férias marcadas neste período."
                }
              </div>
            </div>
          ) : (
            <>
              <div className="timesheet-4-cols mt-5">
                {chartData && (
                  <Chart
                    chartType="Timeline"
                    data={chartData}
                    width="100%"
                    height={"50vh"}
                    options={{
                      timeline: {
                        groupByRowLabel: true,
                        showBarLabels: false,

                        // labelWidth: 400,
                        rowLabelStyle: {
                          fontName: "Arial",
                          fontSize: 10,
                          color: "#333333",
                          textAlign: "left",
                        },
                      },
                      tooltip: { isHtml: true },
                      avoidOverlappingGridLines: false,
                      colors,
                      locale: "pt-BR",
                    }}
                    chartLanguage="pt"
                    loader={<div>Carregando gráfico...</div>}
                  />
                )}
              </div>
              <div className="timesheet-2-cols">
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    flexWrap: "wrap",
                    fontSize: "10px",
                  }}
                >
                  <b>Legenda:</b>
                  {Object.entries(statusColors).map(([status, color]) => {
                    if (status === "default") return null;
                    if (status === "VacationId" && vacationId === undefined)
                      return null;

                    return (
                      <div
                        key={status}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          fontSize: "10px",
                        }}
                      >
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            backgroundColor: color,
                            borderRadius: 4,
                          }}
                        />
                        <span>{statusLabels[status]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              {isDashboard && (
                <div className="timesheet-2-cols buttons-alignment">
                  <Button
                    label="Aprovar"
                    icon="pi pi-check"
                    // className="p-button-success"
                    onClick={() =>
                      handleApproveOrReject(
                        task == "Pedido"
                          ? VacationStatusEnum.APROVADO
                          : "Cancelado",
                      )
                    }
                  />
                  <Button
                    label="Rejeitar"
                    icon="pi pi-times"
                    className="p-button-danger"
                    onClick={
                      () => setVisibleRejectionReason(true) //
                    }
                  />
                </div>
              )}
            </>
          )}
        </>
      )}
      <Toast ref={toast} />
      <Dialog
        header={
          task == "Pedido"
            ? "Rejeição do Pedido de Férias"
            : "Rejeição do Pedido de Cancelamento de Férias"
        }
        visible={visibleRejectionReason}
        style={{ width: "30vw" }}
        modal
        onHide={() => setVisibleRejectionReason(false)}
      >
        <div className="p-fluid">
          <div className="field">
            <label htmlFor="motivo">Informe o Motivo da Rejeição:</label>
            <InputTextarea
              id="motivo"
              value={reasonReject}
              onChange={(e) => setReasonReject(e.target.value)}
              rows={4}
              autoFocus
              maxLength={190}
            />
            <div className="flex justify-content-end">
              <small>{reasonReject?.length}/190 caracteres</small>
            </div>
          </div>
          <div className="flex justify-content-end gap-2 mt-3">
            <Button
              label="Cancelar"
              icon="pi pi-times"
              // className="p-button-text"
              onClick={() => setVisibleRejectionReason(false)}
            />
            <Button
              label="Confirmar"
              icon="pi pi-check"
              onClick={() => {
                if (reasonReject.trim() !== "") {
                  handleApproveOrReject(
                    task == "Pedido"
                      ? VacationStatusEnum.NAO_APROVADO
                      : VacationStatusEnum.APROVADO,
                  );
                } else {
                  toast.current?.show({
                    severity: "error",
                    summary: "Erro",
                    detail:
                      task == "Pedido"
                        ? "Informe o motivo da rejeição do pedido de férias"
                        : "Informe o motivo da rejeição do pedido de cancelamento de férias",
                    life: 6000,
                  });
                }
              }}
              autoFocus
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};
